// src/components/ai-prayer/companion/RealtimeChatInterface.tsx
import { useState, useRef, useEffect } from 'react';
import { toast } from '@/components/ui/sonner';
import { startRealtimeSession } from '@/lib/realtime/startRealtimeSession';
import { useAbielAudio } from '@/components/audio/AbielAudioContext';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Mic, MicOff, Phone, PhoneOff } from 'lucide-react';
import { DEFAULT_VOICE, getRealtimeVoice } from '@/constants/voiceConstants';
import AbielGuidanceDisplay from './AbielGuidanceDisplay';
import RiskAlertBanner from './RiskAlertBanner';

// ✅ unified imports
import { detectRiskFlag, type RiskFlag } from '@/utils/risk';

type DataEvent = any;

export default function RealtimeChatInterface() {
  const { user } = useAuth();
  const { attachLiveStream, detachLiveStream, isLive } = useAbielAudio();

  const [session, setSession] = useState<any>(null);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isMicEnabled, setIsMicEnabled] = useState(true);
  const [guidance, setGuidance] = useState<any>(null);

  // ✅ unified RiskFlag object
  const [riskFlag, setRiskFlag] = useState<RiskFlag | null>(null);

  // Track an accumulating transcript for client-side risk fallback
  const rollingTextRef = useRef<string>("");

  // ✅ Show toast when risk is detected
  useEffect(() => {
    if (riskFlag) {
      toast.error(`⚠️ Sensitive issue detected: ${riskFlag.category.toUpperCase()}`);
    }
  }, [riskFlag]);

  // Token getter using Supabase client
  const getAuthToken = async (): Promise<string> => {
    if (!user) throw new Error('User not authenticated');
    const { supabase } = await import('@/integrations/supabase/client');
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) throw new Error('No valid session token');
    return session.access_token;
  };

  // Extract human-readable text from Realtime data events
  const extractTextFromEvent = (evt: DataEvent): string | null => {
    if (!evt) return null;
    if (evt.mode === 'guide') return null;
    if (evt.type === 'response.output_text.delta' && typeof evt.delta === 'string') {
      return evt.delta;
    }
    if (evt.type === 'response.completed' && Array.isArray(evt.output_text)) {
      try {
        return evt.output_text.join(' ');
      } catch {}
    }
    if (typeof evt.text === 'string') return evt.text;
    if (evt.type?.startsWith('transcript') && typeof evt.content === 'string') {
      return evt.content;
    }
    if (evt.type?.startsWith('input') && typeof evt.transcript === 'string') {
      return evt.transcript;
    }
    if (evt.message && typeof evt.message === 'string') {
      return evt.message;
    }
    return null;
  };

  const handleDataMessage = (message: DataEvent) => {
    try {
      // 1) Server-pushed guidance
      if (message?.mode === 'guide') {
        setGuidance(message);
        if (typeof message.summary === 'string' && !riskFlag) {
          const flag = detectRiskFlag(message.summary);
          if (flag) setRiskFlag(flag);
        }
        return;
      }

      // 2) Client-side risk detection on rolling text
      const chunk = extractTextFromEvent(message);
      if (chunk && chunk.trim()) {
        rollingTextRef.current = (rollingTextRef.current + " " + chunk).slice(-2000);
        if (!riskFlag) {
          const flag = detectRiskFlag(rollingTextRef.current);
          if (flag) {
            console.info('[risk] RT set from rolling transcript:', flag);
            setRiskFlag(flag);
          }
        }
      }
    } catch (e) {
      console.warn('[RealtimeChatInterface] onDataMessage handler error:', e);
    }
  };

  const startConversation = async () => {
    console.log('[Realtime] Starting conversation...', {
      hasMessage: false,
      expectingRisk: false
    });
    setConnecting(true);
    setError(null);
    setGuidance(null);
    setRiskFlag(null);
    rollingTextRef.current = "";

    try {
      const realtimeSession = await startRealtimeSession({
        getAuthToken,
        onRemoteStream: attachLiveStream,
        onDataMessage: handleDataMessage,
        voice: getRealtimeVoice(DEFAULT_VOICE),
      });

      // ✅ Capture initial EF metadata
      if (realtimeSession.riskFlag && !riskFlag) {
        console.info('[risk] RT set from EF:', realtimeSession.riskFlag);
        // Trust the edge function's risk detection; don't re-scan
        setRiskFlag(
          typeof realtimeSession.riskFlag === 'object' && 'category' in realtimeSession.riskFlag
            ? realtimeSession.riskFlag
            : {
                category: realtimeSession.riskFlag as 'suicide' | 'depression' | 'abuse' | 'crisis_general',
                link: 'https://findahelpline.com/?utm_source=chatgpt.com&utm_placement=banner'
              }
        );
      }
      if (realtimeSession.guidance) {
        try {
          const parsed = typeof realtimeSession.guidance === 'string'
            ? JSON.parse(realtimeSession.guidance)
            : realtimeSession.guidance;
          setGuidance(parsed);
        } catch {
          setGuidance(realtimeSession.guidance);
        }
      }

      setSession(realtimeSession);
    } catch (err: any) {
      console.error('[RealtimeChatInterface] Failed to start conversation:', err);
      setError(err?.message ?? 'Failed to start conversation');
    } finally {
      setConnecting(false);
    }
  };

  const endConversation = () => {
    try {
      session?.stop?.();
    } catch (err) {
      console.warn('[RealtimeChatInterface] Error stopping session:', err);
    }
    detachLiveStream();
    setSession(null);
    setIsMicEnabled(true);
    setGuidance(null);
    setRiskFlag(null);
    rollingTextRef.current = "";
  };

  const toggleMicrophone = () => {
    if (session?.microphone) {
      const tracks = session.microphone.getTracks();
      tracks.forEach((track: MediaStreamTrack) => {
        track.enabled = !isMicEnabled;
      });
      setIsMicEnabled(!isMicEnabled);
    }
  };

  return (
    <div className="flex flex-col items-center space-y-6 p-6">
      {/* Header */}
      <div className="text-center space-y-2">
        <h3 className="text-lg font-semibold text-foreground">Speech to Speech</h3>
        <p className="text-sm text-muted-foreground max-w-md">
          AI-assisted; Scripture-led. Live mode never synthesizes ESV audio.
          Have a natural conversation with your prayer companion.
        </p>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 text-center">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      {/* 🚨 Risk Alert */}
      {riskFlag && (
        <RiskAlertBanner
          riskFlag={riskFlag}
          className="w-full max-w-2xl"
        />
      )}

      {/* Connection Status */}
      {(session || isLive) && (
        <div className="flex items-center space-x-2 text-sm">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
          <span className="text-muted-foreground">Connected — Speak naturally</span>
        </div>
      )}

      {/* Main Controls */}
      <div className="flex flex-col items-center space-y-4">
        {!session ? (
          <Button
            onClick={startConversation}
            disabled={connecting || !user}
            size="lg"
            className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-3"
          >
            <Phone className="w-4 h-4 mr-2" />
            {connecting ? 'Connecting…' : 'Start Live Companion'}
          </Button>
        ) : (
          <div className="flex space-x-3">
            <Button
              onClick={toggleMicrophone}
              variant={isMicEnabled ? 'secondary' : 'destructive'}
              size="lg"
            >
              {isMicEnabled ? <Mic className="w-4 h-4 mr-2" /> : <MicOff className="w-4 h-4 mr-2" />}
              {isMicEnabled ? 'Mute' : 'Unmute'}
            </Button>
            <Button onClick={endConversation} variant="outline" size="lg">
              <PhoneOff className="w-4 h-4 mr-2" />
              End Session
            </Button>
          </div>
        )}
      </div>

      {/* Guidance */}
      {guidance && (
        <div className="w-full max-w-2xl">
          <AbielGuidanceDisplay guidance={guidance} />
        </div>
      )}

      {/* Usage Notes */}
      <div className="text-xs text-muted-foreground text-center space-y-1 max-w-sm">
        <p>• Microphone access required for conversation</p>
        <p>• Session may auto-end after inactivity</p>
        <p>• AI responses are spiritually guided but not infallible</p>
      </div>

      {/* Provider Info */}
      <div className="bg-primary/10 border border-primary/20 rounded-lg p-3 text-center max-w-sm">
        <p className="text-xs text-primary font-medium">Powered by OpenAI Real-time API</p>
        <p className="text-xs text-muted-foreground mt-1">
          This mode uses OpenAI's specialized real-time technology exclusively.
          Provider settings don't apply to live conversations.
        </p>
      </div>

      {/* Feature Flag Notice */}
      {import.meta.env.VITE_ABIEL_REALTIME_ENABLED !== 'true' && (
        <div className="bg-warning/10 border border-warning/20 rounded-lg p-3 text-center">
          <p className="text-xs text-warning">
            Realtime mode disabled. Set VITE_ABIEL_REALTIME_ENABLED=true to enable.
          </p>
        </div>
      )}
    </div>
  );
}
